import { AxiosInstance } from 'axios';
import { ObjectType } from './api';

// This client is kept apart from api.ts because that file is generated by nswag and gets
// overwritten on the next generation.
//
// The axios instance was created with transformResponse: [], so response.data is a string
// and has to be parsed here.

export interface RequeueItem {
    objectType: ObjectType | string;
    eventType: string;
    objectGuid: string;
    relationId: string;
}

export interface RequeueItemResult {
    message: string;
    relationId: string;
    objectType: string;
    queueMessageId?: string;
    insertedOn?: string;
    sent: boolean;
    error?: string;
}

export interface RequeueResult {
    queueName: string;
    requested: number;
    unique: number;
    queued: number;
    /**
     * How many the API refused because this queue's consumer has no handler for their type.
     *
     * Not a failure: sending them would have removed them from the queue without a log row
     * and recorded the objects as synchronised, which is the one outcome nobody can spot
     * afterwards. Each refused item carries the reason in its own `error`.
     */
    refused: number;
    /**
     * How many the API held back because offering them again cannot succeed - the stored
     * copy is gone - or would book a payment a chargeback removed. Each carries its reason.
     */
    blocked?: number;
    queueDepthAfter: number;
    results: RequeueItemResult[];
}

export interface SyncStatus {
    objectType: string;
    relationId: string;
    syncedToExact: boolean;
    dependingId?: string;
    relationLastModified?: string;
    lastState?: string;
    lastMessage?: string;
    failCount: number;
    syncLogTimestamp?: string;
    queueDepth: number;
    poisonQueueDepth: number;
    summary: string;
}

/** The status as the batch endpoint returns it: the relation state only. */
export interface SyncStatusSummary {
    objectType: string;
    relationId: string;
    syncedToExact: boolean;
    relationExists: boolean;
    dependingId?: string;
    relationLastModified?: string;
    summary: string;
}

export interface SyncStatusBatch {
    queueName: string;
    queueDepth: number;
    poisonQueueDepth: number;
    requested: number;
    synced: number;
    items: SyncStatusSummary[];
}

/**
 * Which transfer queue each object type belongs on.
 *
 * A mirror of `PipelineRouting` on the server, which is itself a copy of the two
 * `CanBeProcessed` implementations. Duplicated here on purpose: the API is the one that
 * refuses, but a screen that can only find out by asking would have to send the message to
 * discover it may not - so this is what lets the bar say up front where a selection is
 * headed, and the server stays the thing that enforces it.
 *
 * The direction matters more than it looks. Both send handlers open with
 * `if (!CanBeProcessed(message)) return;`, after which the function carries on to
 * `SyncSuccessfulLogAsync` - so a message on the wrong queue is deleted and the object is
 * recorded as successfully synchronised. There is no error and no poison message; the
 * mistake is invisible afterwards.
 */
const TOWARDS_EXACT = new Set([
    'Customer',
    'CreditNote',
    'CreditNoteSalesEntry',
    'Invoice',
    'InvoiceSalesEntry',
    'PaymentOnline',
    'PaymentOnlineCredit',
    'PaymentOnlineChargeback',
    'Product',
]);

const TOWARDS_CHARGEBEE = new Set([
    'Payment',
    'PaymentSalesEntry',
    'Chargeback',
    'Customer',
]);

/** The queue read by send-exact, and therefore the road towards Exact. */
export const QUEUE_TOWARDS_EXACT = 'chargebee';

/** The queue read by send-chargebee, and therefore the road towards Chargebee. */
export const QUEUE_TOWARDS_CHARGEBEE = 'exact';

/** Where a type of object can travel. */
export type PipelineRoute =
    | 'None'
    | 'TowardsExact'
    | 'TowardsChargebee'
    | 'Both';

export const routeOf = (objectType: ObjectType | string): PipelineRoute => {
    const name = String(objectType);
    const toExact = TOWARDS_EXACT.has(name);
    const toChargebee = TOWARDS_CHARGEBEE.has(name);

    return toExact && toChargebee
        ? 'Both'
        : toExact
        ? 'TowardsExact'
        : toChargebee
        ? 'TowardsChargebee'
        : 'None';
};

/**
 * The queue this type belongs on, or null when that is ambiguous or nowhere at all.
 *
 * A customer is handled by both send handlers, so its queue follows from which system
 * changed it rather than from its type - and nothing in the tables records that. Null is
 * the honest answer, and the bar asks rather than guessing.
 */
export const queueForObjectType = (
    objectType: ObjectType | string
): string | null => {
    switch (routeOf(objectType)) {
        case 'TowardsExact':
            return QUEUE_TOWARDS_EXACT;
        case 'TowardsChargebee':
            return QUEUE_TOWARDS_CHARGEBEE;
        default:
            return null;
    }
};

/** A relationId is only unique within its own objectType. */
export const statusKey = (
    objectType: ObjectType | string | undefined,
    relationId: string | undefined
) => `${objectType ?? ''}|${relationId ?? ''}`;

const parse = <T,>(data: any): T =>
    typeof data === 'string' ? (JSON.parse(data) as T) : (data as T);

/** Pull the error message out of a failed call, even when it comes back as a string. */
export const describeError = (error: any): string => {
    const data = error?.response?.data;

    if (typeof data === 'string' && data.length > 0) {
        return data;
    }

    return error?.message ?? 'Onbekende fout';
};

export const requeueItems = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    items: RequeueItem[],
    queueName: string,
    dryRun: boolean
): Promise<RequeueResult> => {
    const response = await axios.post(
        `/QueueLog/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/requeue`,
        { items, queueName, dryRun }
    );

    return parse<RequeueResult>(response.data);
};

export const getSyncStatus = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    objectType: string,
    relationId: string,
    queueName: string
): Promise<SyncStatus> => {
    const response = await axios.get(
        `/QueueLog/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/status/${encodeURIComponent(objectType)}/${encodeURIComponent(
            relationId
        )}`,
        { params: { queueName } }
    );

    return parse<SyncStatus>(response.data);
};

/**
 * Ask for the state of many objects in a single call. Polling per object costs five table
 * queries and two queue calls on the server side, which floors both the API and the
 * browser once you have thousands of objects.
 */
export const getSyncStatusBatch = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    items: { objectType: ObjectType | string; relationId: string }[],
    queueName: string
): Promise<SyncStatusBatch> => {
    const response = await axios.post(
        `/QueueLog/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/status/batch`,
        { items, queueName }
    );

    return parse<SyncStatusBatch>(response.data);
};
